import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { ArrowLeft, Loader2, ShieldAlert, CheckCircle2, XCircle, Eye, Users } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import {
  adminListPayouts,
  adminGetPayoutDetail,
  adminMarkPayoutPaid,
  adminRejectPayout,
} from "@/lib/wallet.functions";

export const Route = createFileRoute("/admin/payouts")({
  head: () => ({ meta: [{ title: "Admin · Payouts — ZYNTRA" }] }),
  component: AdminPayouts,
});

async function withAuth<T>(fn: (h: any) => Promise<T>): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data?.session?.access_token;
  return fn({ authorization: token ? `Bearer ${token}` : "" });
}

function fmt(n: number, currency = "CRC") {
  return `${currency} ${Number(n || 0).toLocaleString("es-CR", { maximumFractionDigits: 0 })}`;
}

function AdminPayouts() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>("pending");
  const [payouts, setPayouts] = useState<any[]>([]);
  const [detail, setDetail] = useState<any | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate({ to: "/login" }); return; }
    (async () => {
      const { data } = await supabase.from("user_roles").select("role").eq("user_id", user.id).eq("role", "admin").maybeSingle();
      setIsAdmin(!!data);
      if (data) await load("pending");
      setLoading(false);
    })();
  }, [user, authLoading]);

  async function load(status: string) {
    setFilter(status);
    try {
      const { payouts } = await withAuth((h) => adminListPayouts({ data: { status: status === "all" ? "" : status }, headers: h } as any));
      setPayouts(payouts);
    } catch (e) {
      console.error(e);
    }
  }

  if (authLoading || loading) return <div className="flex min-h-[60vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-md px-4 pt-32 text-center">
        <ShieldAlert className="mx-auto h-10 w-10 text-muted-foreground" />
        <h1 className="mt-3 text-xl font-bold text-foreground">Acceso restringido</h1>
        <Link to="/dashboard" className="mt-4 inline-block text-sm text-primary hover:underline">Volver al dashboard</Link>
      </div>
    );
  }

  const totalPending = payouts.filter((p) => p.status === "pending").reduce((s, p) => s + Number(p.amount || 0), 0);

  return (
    <div className="mx-auto max-w-6xl px-4 pb-20 pt-24 sm:px-6">
      <Link to="/dashboard/admin" className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> Volver
      </Link>

      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="text-2xl font-black text-foreground sm:text-3xl">Solicitudes de payout</h1>
        <Link to="/admin/payouts/users" className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-secondary px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-accent">
          <Users className="h-3.5 w-3.5" /> Por usuario
        </Link>
      </div>

      <div className="grid gap-3 sm:grid-cols-3 mb-6">
        <SummaryCard label="Pendiente total" value={fmt(totalPending)} highlight />
        <SummaryCard label="Solicitudes pendientes" value={String(payouts.filter((p) => p.status === "pending").length)} />
        <SummaryCard label="Mostradas" value={String(payouts.length)} />
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {["pending", "paid", "rejected", "all"].map((s) => (
          <button
            key={s}
            onClick={() => load(s)}
            className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
              filter === s ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground hover:bg-accent"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-3 text-left">Usuario</th>
                <th className="px-4 py-3 text-left">Monto</th>
                <th className="px-4 py-3 text-left">Método</th>
                <th className="px-4 py-3 text-left">Estado</th>
                <th className="px-4 py-3 text-left">Solicitado</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {payouts.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Sin solicitudes</td></tr>
              )}
              {payouts.map((p) => {
                const ms = p.method_snapshot || {};
                return (
                  <tr key={p.id} className="border-t border-border hover:bg-accent/30">
                    <td className="px-4 py-3">
                      <p className="font-medium text-foreground">{p.display_name || p.email}</p>
                      <p className="text-xs text-muted-foreground">{p.email}</p>
                    </td>
                    <td className="px-4 py-3 font-bold text-primary">{fmt(p.amount, p.currency)}</td>
                    <td className="px-4 py-3 text-xs text-foreground">
                      {ms.method || "—"}
                      {ms.sinpe_number && <p className="text-muted-foreground">{ms.sinpe_number}</p>}
                      {ms.bank_name && <p className="text-muted-foreground">{ms.bank_name}</p>}
                      {ms.iban && <p className="text-muted-foreground font-mono">{ms.iban}</p>}
                    </td>
                    <td className="px-4 py-3"><PayoutStatusBadge status={p.status} /></td>
                    <td className="px-4 py-3 text-xs text-muted-foreground">{new Date(p.requested_at).toLocaleString("es-CR")}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={async () => {
                          const { request, profile, wallet } = await withAuth((h) => adminGetPayoutDetail({ data: { requestId: p.id }, headers: h } as any));
                          setDetail({ request, profile, wallet, listRow: p });
                        }}
                        className="inline-flex items-center gap-1 rounded-lg bg-secondary px-2.5 py-1.5 text-xs font-semibold text-foreground hover:bg-accent"
                      >
                        <Eye className="h-3.5 w-3.5" /> Ver
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {detail && (
        <DetailDrawer
          detail={detail}
          onClose={() => setDetail(null)}
          onAction={async () => { setDetail(null); await load(filter); }}
        />
      )}
    </div>
  );
}

function SummaryCard({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`mt-2 text-2xl font-black ${highlight ? "gradient-text" : "text-foreground"}`}>{value}</p>
    </div>
  );
}

function PayoutStatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    pending: "bg-warning/20 text-warning",
    paid: "bg-success/20 text-success",
    rejected: "bg-destructive/20 text-destructive",
    cancelled: "bg-muted text-muted-foreground",
  };
  const labels: Record<string, string> = {
    pending: "Pendiente",
    paid: "Pagado",
    rejected: "Rechazado",
    cancelled: "Cancelado",
  };
  return <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${styles[status] || styles.pending}`}>{labels[status] || status}</span>;
}

function DetailDrawer({ detail, onClose, onAction }: { detail: any; onClose: () => void; onAction: () => void }) {
  const [reference, setReference] = useState("");
  const [notes, setNotes] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const r = detail.request;
  const p = detail.profile;
  const w = detail.wallet;

  async function markPaid() {
    setBusy(true); setErr("");
    try {
      await withAuth((h) => adminMarkPayoutPaid({ data: { requestId: r.id, reference, notes }, headers: h } as any));
      onAction();
    } catch (e: any) { setErr(e.message); setBusy(false); }
  }
  async function reject() {
    if (!reason.trim()) { setErr("Indicá el motivo del rechazo."); return; }
    setBusy(true); setErr("");
    try {
      await withAuth((h) => adminRejectPayout({ data: { requestId: r.id, reason }, headers: h } as any));
      onAction();
    } catch (e: any) { setErr(e.message); setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 sm:items-center" onClick={onClose}>
      <div className="w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl border border-border bg-card p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-foreground">Detalle de payout</h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><XCircle className="h-5 w-5" /></button>
        </div>

        <div className="space-y-3 text-sm">
          <Row k="Usuario" v={detail.listRow.display_name || detail.listRow.email} />
          <Row k="Email" v={detail.listRow.email} />
          <Row k="Monto" v={<span className="font-bold text-primary">{fmt(r.amount, r.currency)}</span>} />
          <Row k="Estado" v={<PayoutStatusBadge status={r.status} />} />
          <Row k="Wallet disp." v={w ? fmt(w.available_balance, w.currency) : "—"} />
          <Row k="Solicitado" v={new Date(r.requested_at).toLocaleString("es-CR")} />
        </div>

        <div className="mt-4 rounded-lg border border-border bg-secondary/40 p-3 text-xs">
          <p className="font-semibold text-foreground mb-2">Datos de pago</p>
          {p ? (
            <div className="space-y-1 text-muted-foreground">
              <p><strong className="text-foreground">Titular:</strong> {p.full_name}</p>
              <p><strong className="text-foreground">Método:</strong> {p.method}</p>
              {p.document_id && <p><strong className="text-foreground">Doc:</strong> {p.document_id}</p>}
              {p.sinpe_number && <p><strong className="text-foreground">SINPE:</strong> {p.sinpe_number}</p>}
              {p.bank_name && <p><strong className="text-foreground">Banco:</strong> {p.bank_name}</p>}
              {p.account_number && <p><strong className="text-foreground">Cuenta:</strong> {p.account_number}</p>}
              {p.iban && <p><strong className="text-foreground">IBAN:</strong> <span className="font-mono">{p.iban}</span></p>}
              {p.email_for_payment && <p><strong className="text-foreground">Email:</strong> {p.email_for_payment}</p>}
              {p.notes && <p><strong className="text-foreground">Notas:</strong> {p.notes}</p>}
            </div>
          ) : <p className="text-muted-foreground">Sin perfil de payout configurado.</p>}
        </div>

        {r.status === "pending" && (
          <div className="mt-4 space-y-3">
            <div>
              <label className="text-xs font-semibold text-foreground">Referencia / comprobante</label>
              <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Ej: TRX-12345" className="mt-1 w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none" />
            </div>
            <div>
              <label className="text-xs font-semibold text-foreground">Notas internas</label>
              <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none" />
            </div>
            <div>
              <label className="text-xs font-semibold text-foreground">Motivo de rechazo (si aplica)</label>
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Solo si vas a rechazar" className="mt-1 w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none" />
            </div>
            {err && <p className="rounded-lg bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</p>}
            <div className="flex gap-2">
              <button onClick={markPaid} disabled={busy} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl gradient-primary py-2.5 text-sm font-bold text-primary-foreground disabled:opacity-50">
                <CheckCircle2 className="h-4 w-4" /> Marcar pagado
              </button>
              <button onClick={reject} disabled={busy} className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-xl border border-destructive/30 bg-destructive/10 py-2.5 text-sm font-bold text-destructive disabled:opacity-50">
                <XCircle className="h-4 w-4" /> Rechazar
              </button>
            </div>
          </div>
        )}

        {r.status !== "pending" && (
          <div className="mt-4 space-y-2 rounded-lg border border-border bg-secondary/40 p-3 text-xs">
            {r.reference_number && <p><strong className="text-foreground">Referencia:</strong> {r.reference_number}</p>}
            {r.admin_notes && <p><strong className="text-foreground">Notas:</strong> {r.admin_notes}</p>}
            {r.paid_at && <p><strong className="text-foreground">Pagado:</strong> {new Date(r.paid_at).toLocaleString("es-CR")}</p>}
            {r.reviewed_at && !r.paid_at && <p><strong className="text-foreground">Revisado:</strong> {new Date(r.reviewed_at).toLocaleString("es-CR")}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{k}</span>
      <span className="text-foreground">{v}</span>
    </div>
  );
}
